/**
 * @typedef {{
 *     value: string,
 *     label: string
 * }} LySelectOption
 */

/** @type {string[]} Nullbreite Zeichen */
const ZERO_WIDTH_CHARS = ['\uFEFF', '\u200B', '\u200C', '\u200D', '\u180E'];

/** @type {string[]} "Leerzeichen-artige" Zeichen */
const SPACE_LIKE_CHARS = [
    '\u00A0',
    '\u1680',
    '\u2000',
    '\u2001',
    '\u2002',
    '\u2003',
    '\u2004',
    '\u2005',
    '\u2006',
    '\u2007',
    '\u2008',
    '\u2009',
    '\u200A',
    '\u202F',
    '\u205F',
    '\u3000',
    '\u2028',
    '\u2029',
    '\v',
];

const ZERO_WIDTH_PATTERN = new RegExp(`[${ZERO_WIDTH_CHARS.join('')}]`, 'gu');
const SPACE_LIKE_PATTERN = new RegExp(`[${SPACE_LIKE_CHARS.join('')}]`, 'gu');

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_PATTERN = /[\x00-\x08\x0E-\x1F\x7F-\x9F]/gu;
const MULTI_SPACE_PATTERN = / {2,}/g;

/** @type {{ [key: string]: string }} */
export const LineMode = Object.freeze({
    ONE_LINE: 'one-line', // Einzeilig
    MULTI_NO_EMPTY: 'multi-no-empty', // Mehrzeilen (keine Leerzeilen)
    MULTI_MAX_ONE_EMPTY: 'multi-max-one-empty', // Mehrzeilen (max. 1 Leerzeile) [STANDARD]
    MULTI_ANY_EMPTY: 'multi-any-empty', // Mehrzeilen (beliebig viele Leerzeilen)
});

/** @type {{ [key: string]: string }} */
export const SpaceMode = Object.freeze({
    COLLAPSE: 'collapse', // Doppelte Leerzeichen zusammenführen [STANDARD]
    PRESERVE: 'preserve', // Doppelte Leerzeichen erhalten
});

/** @type {{ [key: string]: string }} */
export const IndentMode = Object.freeze({
    REMOVE: 'remove', // Führende Leerzeichen entfernen [STANDARD]
    PRESERVE: 'preserve', // Führende Leerzeichen erhalten
});

/** @type {LySelectOption[]} */
export const LINE_MODE_OPTIONS = [
    { value: LineMode.ONE_LINE, label: 'Einzeilig' },
    { value: LineMode.MULTI_NO_EMPTY, label: 'Mehrzeilen (keine Leerzeilen)' },
    { value: LineMode.MULTI_MAX_ONE_EMPTY, label: 'Mehrzeilen (maximal 1 Leerzeile)' },
    { value: LineMode.MULTI_ANY_EMPTY, label: 'Mehrzeilen (beliebig viele Leerzeilen)' },
];

/** @type {LySelectOption[]} */
export const SPACE_MODE_OPTIONS = [
    { value: SpaceMode.COLLAPSE, label: 'Zusammenführen' },
    { value: SpaceMode.PRESERVE, label: 'Erhalten' },
];

/** @type {LySelectOption[]} */
export const INDENT_MODE_OPTIONS = [
    { value: IndentMode.REMOVE, label: 'Entfernen' },
    { value: IndentMode.PRESERVE, label: 'Erhalten' },
];

export class NormalizeService {
    /**
     * Entfernt Nullbreite-/Steuerzeichen und faltet exotische Spaces zu einem normalen Space.
     * @param {string} subject
     * @returns {string}
     */
    static stripInvisible(subject) {
        return subject.replace(ZERO_WIDTH_PATTERN, '').replace(CONTROL_CHAR_PATTERN, '').replace(SPACE_LIKE_PATTERN, ' ');
    }

    /**
     * Normalisiert eine einzelne Zeile (Tabs → Space, ggf. Space-Runs kollabieren,
     * Trailing-Whitespace immer weg, Leading-Whitespace optional erhalten).
     * @param {string} line
     * @param {string} spaceMode
     * @param {string} indentMode
     * @returns {string}
     */
    static normalizeLine(line, spaceMode, indentMode = IndentMode.REMOVE) {
        let result = line.replace(/\t+/g, ' ');

        if (spaceMode === SpaceMode.COLLAPSE) {
            result = result.replace(MULTI_SPACE_PATTERN, ' ');
        }

        // Trailing-Whitespace ist immer unsichtbar und wird immer entfernt
        result = result.replace(/[ \t]+$/, '');

        // Zeile besteht nur aus Whitespace → als leere Zeile behandeln
        if (result.trim() === '') {
            return '';
        }

        if (indentMode === IndentMode.REMOVE) {
            result = result.replace(/^[ \t]+/, '');
        }

        return result;
    }

    /**
     * Reduziert aufeinanderfolgende Leerzeilen auf maximal eine.
     * @param {string[]} lines
     * @returns {string[]}
     */
    static collapseEmptyLines(lines) {
        /** @type {string[]} */
        const tokens = [];
        let lastIsEmpty = true; // führende Leerzeilen sollen ebenfalls wegfallen

        for (const line of lines) {
            const thisIsEmpty = line === '';
            if (lastIsEmpty && thisIsEmpty) {
                continue;
            }
            lastIsEmpty = thisIsEmpty;
            tokens.push(line);
        }

        // trailing Leerzeile(n) am Ende noch abschneiden
        while (tokens.length > 0 && tokens[tokens.length - 1] === '') {
            tokens.pop();
        }

        return tokens;
    }
    /**
     * @param {?string} subject
     * @param {string} lineMode
     * @param {string} spaceMode
     * @param {string} indentMode
     * @returns {?string}
     */
    static normalize(subject, lineMode = LineMode.MULTI_MAX_ONE_EMPTY, spaceMode = SpaceMode.COLLAPSE, indentMode = IndentMode.REMOVE) {
        if (subject === null || subject === '') {
            return subject;
        }

        let text = subject.normalize('NFC');
        text = text.replace(/\r\n|\r/g, '\n');
        text = this.stripInvisible(text);

        if (lineMode === LineMode.ONE_LINE) {
            text = text.replace(/\n+/g, ' ');
            return this.normalizeLine(text, spaceMode, indentMode);
        }

        const lines = text.split('\n').map((line) => this.normalizeLine(line, spaceMode, indentMode));

        if (lineMode === LineMode.MULTI_NO_EMPTY) {
            return lines
                .filter((line) => line !== '')
                .join('\n')
                .replace(/^\n+|\n+$/g, '');
        }

        if (lineMode === LineMode.MULTI_MAX_ONE_EMPTY) {
            return this.collapseEmptyLines(lines).join('\n');
        }

        let start = 0;
        let end = lines.length;
        while (start < end && lines[start] === '') start++;
        while (end > start && lines[end - 1] === '') end--;

        return lines.slice(start, end).join('\n');
    }
}
